import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import { ArrowRight } from 'lucide-react';

const ToolCard = ({ tool }) => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  const isDisabled = tool?.disabled === true;

  const handleLaunchTool = () => {
    if (isDisabled) return;
    navigate(tool?.path);
  };

  const handleClick = (e) => {
    if (isDisabled) return;
    // Prevent navigation if clicking on the button directly
    if (e?.target?.closest('button')) return;
    handleLaunchTool();
  };

  // Check if this tool should have the gradient effect
  const hasGradientEffect = [
    "Moodboard to Room",
    "CAD-Based 3D Room Generator",
    "Sketch to 3D Interior",
    "Room Variation Generator",
    "Combine Interior Styles",
    "Furniture and Materials editing",
    "Site Concept",
    "Ground-Up Complete Room Design"
  ]?.includes(tool?.name);

  // Generate text shadow for faint ivory cream gradient glow
  const getTextShadow = () => {
    if (!hasGradientEffect || !isHovered) return 'none';
    
    // Create multiple layers of text-shadow with #fffcef at decreasing opacity (2% to 0%)
    return `
      0 0 8px rgba(255, 252, 239, 0.02),
      0 0 16px rgba(255, 252, 239, 0.015),
      0 0 24px rgba(255, 252, 239, 0.01),
      0 0 32px rgba(255, 252, 239, 0.005),
      0 0 40px rgba(255, 252, 239, 0.002)
    `;
  };

  return (
    <div
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`group relative rounded-xl lg:rounded-2xl overflow-hidden transition-all duration-300 flex flex-col h-full ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
      style={{
        background: 'rgba(255, 255, 255, 0.03)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: isHovered 
          ? '0 8px 32px 0 rgba(0, 0, 0, 0.37), inset 0 1px 1px 0 rgba(255, 255, 255, 0.05), 0 0 20px rgba(254, 131, 56, 0.15)'
          : '0 8px 32px 0 rgba(0, 0, 0, 0.37), inset 0 1px 1px 0 rgba(255, 255, 255, 0.05)'
      }}
    >
      <div className="relative h-32 md:h-48 lg:h-64 overflow-hidden">
        <Image
          src={tool?.image}
          alt={tool?.imageAlt}
          className="w-full h-full object-cover transition-opacity duration-700"
          style={{
            opacity: tool?.hoverImage ? (isHovered ? 0 : 1) : 1,
          }}
        />
        {tool?.hoverImage && (
          <Image
            src={tool.hoverImage}
            alt={tool?.imageAlt}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
            style={{
              opacity: isHovered ? 1 : 0,
            }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#292A2B] via-transparent to-transparent" style={{ backgroundSize: '100% 15%', backgroundPosition: 'bottom', backgroundRepeat: 'no-repeat' }} />
        {isDisabled && (
          <>
            <div className="absolute inset-0 bg-black/60 z-10" />
            <span className="absolute top-3 left-3 z-20 px-2.5 py-1 rounded-md text-[10px] md:text-xs font-semibold bg-red-500/90 text-white uppercase tracking-wide">
              Disabled
            </span>
          </>
        )}
      </div>
      <div 
        className="p-3 md:p-4 lg:p-6 relative flex flex-col flex-1"
        style={{
          background: 'linear-gradient(to bottom, rgba(41, 42, 43, 0.95) 0%, rgba(41, 42, 43, 0.85) 50%, rgba(41, 42, 43, 0) 100%)'
        }}
      >
        <h3 
          className="text-base font-bold text-foreground mb-2 md:mb-4 lg:mb-6 group-hover:text-luxury-orange-light transition-colors"
          style={{
            textShadow: getTextShadow(),
            transition: 'text-shadow 0.3s ease'
          }}
        >
          {/* Mobile: Break specific tool names into two lines */}
          <span className="lg:hidden">
            {tool?.name === "Moodboard to Room" ? (
              <>
                Moodboard<br />to Room
              </>
            ) : tool?.name === "Sketch to 3D Interior" ? (
              <>
                Sketch to<br />3D Interior
              </>
            ) : tool?.name === "Combine Interior Styles" ? (
              <>
                Combine<br />Interior Styles
              </>
            ) : (
              tool?.name
            )}
          </span>
          {/* Desktop: Show full name on one line */}
          <span className="hidden lg:inline text-xl text-[rgba(255,252,239,1)]">
            {tool?.name}
          </span>
        </h3>

        <p className="text-[10px] md:text-xs lg:text-sm mb-2 md:mb-4 lg:mb-6 leading-relaxed md:h-9 lg:h-10 md:line-clamp-2 text-[rgba(255,252,239,1)]">
          {tool?.description}
        </p>

        {tool?.toolsSummary && (
          <div
            className="transition-[grid-template-rows] duration-300 ease-in-out"
            style={{
              display: 'grid',
              gridTemplateRows: showDescription ? '1fr' : '0fr',
            }}
          >
            <div className="overflow-hidden">
              <div className="mb-2 md:mb-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }} />
              <ul className="mb-2 md:mb-3 text-[8px] md:text-[10px] lg:text-xs text-[rgba(255,252,239,1)] leading-relaxed list-disc list-inside space-y-0.5">
                {tool.toolsSummary.split(' • ').map((item, i) => (
                  <li key={i}>{item.trim()}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <div className="flex items-center gap-1.5 md:gap-3 mt-auto">
          <button
            onClick={handleLaunchTool}
            className="flex-[2] py-2 md:py-3 lg:py-4 px-2.5 md:px-4 lg:px-6 rounded-lg md:rounded-2xl flex items-center justify-center gap-2 lg:gap-3 text-white font-medium text-[9px] md:text-sm lg:text-base transition-all duration-300 hover:scale-[1.03] relative overflow-hidden group/btn glassmorphic-button"
            style={{ minHeight: '36px' }}
          >
            <span className="relative z-10 text-[rgba(255,252,239,1)]">{isDisabled ? 'Disabled' : 'Launch Tool'}</span>
            {!isDisabled && <ArrowRight className="w-4 h-4 lg:w-5 lg:h-5 relative z-10 hidden md:block" />}
          </button>

          {tool?.toolsSummary ? (
            <button
              onClick={(e) => { e.stopPropagation(); setShowDescription(!showDescription); }}
              className="flex-[1] py-2 md:py-3 lg:py-4 px-2.5 md:px-4 rounded-lg md:rounded-2xl flex items-center justify-center gap-1.5 text-[9px] md:text-xs lg:text-sm font-medium text-white transition-all duration-300"
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                boxShadow: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.2), inset 0 1px 1px 0 rgba(255, 255, 255, 0.05)',
                minHeight: '36px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                e.currentTarget.style.boxShadow = '0 0 0 2px rgba(254, 131, 56, 0.1), inset 0 2px 4px 0 rgba(0, 0, 0, 0.2), 0 0 15px rgba(254, 131, 56, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                e.currentTarget.style.boxShadow = 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.2), inset 0 1px 1px 0 rgba(255, 255, 255, 0.05)';
              }}
            >
              <ArrowRight className={`w-3 h-3 md:w-3.5 md:h-3.5 transition-transform duration-200 hidden md:block ${showDescription ? 'rotate-90' : ''}`} />
              {showDescription ? 'Hide' : 'Learn more'}
            </button>
          ) : (
            <button
              disabled
              className="flex-[1] py-2 md:py-3 lg:py-4 px-2.5 md:px-4 rounded-lg md:rounded-2xl flex items-center justify-center gap-1.5 text-[9px] md:text-xs lg:text-sm font-medium text-white/30 cursor-default glassmorphic-button-dark"
              style={{ minHeight: '36px', opacity: 0.4 }}
            >
              <ArrowRight className="w-3 h-3 md:w-3.5 md:h-3.5 hidden md:block" />
              Learn more
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ToolCard;